import {createSelector} from '@reduxjs/toolkit'

import {eq} from '@shared/helpers/lang'

import {PendingPaymentStatus} from '@web/core/constants'

import {RootState} from './interfaces'
import {getAccountInfo, getAccountsInfo, isAllBanksUnAvailable} from './utils'

export const accountSelector = (state: RootState) => state.account

/** 은행 전체 점검 정보 selector */
export const accountMaintenanceInfoSelector = createSelector(
    (state: RootState) => state.account.maintenanceInfo,
    (maintenanceInfo) => {
        if (!isAllBanksUnAvailable(maintenanceInfo?.status ?? null)) {
            return null
        }
        return {
            status: maintenanceInfo?.status ?? null,
            /** 은행 점검 시작 시간 */
            startTime: maintenanceInfo?.startTime ?? '',
            /** 은행 점검 완료 시간 */
            endTime: maintenanceInfo?.endTime ?? '',
        }
    },
)

/** 충전 계좌 정보 selector */
export const rechargeAccountInfoSelector = createSelector(
    accountMaintenanceInfoSelector,
    (state: RootState) => state.account.recharge,
    (state: RootState) => state.account.pendingPaymentInfo,
    (maintenanceInfo, {accounts, selectedAccountKey}, {status: pendingPaymentStatus}) => {
        const isPendingPaymentStatusEnable = eq(pendingPaymentStatus, PendingPaymentStatus.ENABLE)

        const {
            accounts: refinedAccounts,
            isRegistered,
            isNaverAccountRegistered,
            hasPayableAccount,
        } = getAccountsInfo(accounts, isPendingPaymentStatusEnable, !!maintenanceInfo)
        const {selectedAccount, selectedAccountIndex, isSelectedNaverAccount, isSelectedAccountPayAvailable} =
            getAccountInfo({
                accounts: refinedAccounts,
                selectedAccountKey,
            })

        return {
            /** 충전 계좌목록 */
            accounts: refinedAccounts,
            selected: {
                /** 선택된 충전 계좌 정보 */
                account: selectedAccount,
                /** 선택된 충전 계좌 key */
                accountKey: selectedAccountKey,
                /** 선택된 충전 계좌 index */
                index: selectedAccountIndex,
                /** 선택한 충전계좌가 네이버 통장인지 여부 */
                isNaverAccount: isSelectedNaverAccount,
                /** 선택한 충전계좌 충전 가능 여부 */
                payAvailable: isRegistered && isSelectedAccountPayAvailable,
            },
            /** 충전 계좌 등록 여부 */
            isRegistered,
            /** 충전 계좌 중 네이버 통장 보유 */
            isNaverAccountRegistered,
            /** 충전 계좌 전체 점검 정보 */
            maintenance: hasPayableAccount ? null : maintenanceInfo,
            /** 비동기 결제 사용 가능 여부 */
            isPendingPaymentStatusEnable,
        }
    },
)

/** 선택된 충전 계좌 key selector */
export const selectedRechargeAccountKeySelector = createSelector(
    (state: RootState) => state.account.recharge,
    ({accounts, selectedAccountKey}) => {
        return selectedAccountKey ?? accounts[0]?.accountKey ?? ''
    },
)

/** 간편 계좌 정보 selector */
export const bankEasyPayAccountInfoSelector = createSelector(
    (state: RootState) => state.account.payServiceInfo,
    accountMaintenanceInfoSelector,
    (state: RootState) => state.account.bankEasyPay,
    (state: RootState) => state.account.pendingPaymentInfo,
    (payServiceInfo, maintenanceInfo, {accounts, selectedAccountKey}, {status: pendingPaymentStatus}) => {
        const isPendingPaymentStatusEnable = eq(pendingPaymentStatus, PendingPaymentStatus.ENABLE)
        const {
            accounts: refinedAccounts,
            isRegistered,
            isNaverAccountRegistered,
            hasPayableAccount,
        } = getAccountsInfo(accounts, isPendingPaymentStatusEnable, !!maintenanceInfo)
        const {selectedAccount, selectedAccountIndex, isSelectedNaverAccount, isSelectedAccountPayAvailable} =
            getAccountInfo({
                accounts: refinedAccounts,
                selectedAccountKey,
            })

        return {
            /** 간편결제 계좌 목록 */
            accounts: refinedAccounts,
            /** 첫번째 계좌 반환 */
            firstAccount: refinedAccounts[0] ?? null,
            selected: {
                /** 선택된 간편결제 계좌 정보 */
                account: selectedAccount,
                /** 선택된 간편결제 계좌 key */
                accountKey: selectedAccountKey,
                /** 선택된 간편결제 계좌 index */
                index: selectedAccountKey ? selectedAccountIndex : accounts.length,
                /** 선택된 간편결제 계좌가 네이버 통장인지 여부 */
                isNaverAccount: isSelectedNaverAccount,
                /** 선택된 간편계좌 결제 가능 여부 */
                payAvailable: isRegistered && isSelectedAccountPayAvailable,
            },
            /** 간편결제 계좌 등록 여부 */
            isRegistered,
            /** 간편결제 계좌 중 네이버 통장 보유 */
            isNaverAccountRegistered,
            /** 계좌정보 조회실패 여부 */
            payServiceInfo,
            /** 간편 계좌 전체 점검 정보 */
            maintenance: hasPayableAccount ? null : maintenanceInfo,
            /** 비동기 결제 사용 가능 여부 */
            isPendingPaymentStatusEnable,
        }
    },
)
